import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import { wastageApi } from './WastageApi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Format date for API (YYYY-MM-DD)
const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Get current date
const getCurrentDate = () => {
  return new Date();
};

// Transform API response to component's expected format
const transformApiResponseToComponentFormat = (apiData, sites = []) => {
  if (!apiData) return null;

  // Transform time series to daywise wastage
  const daywiseWastage = (apiData.timeSeries || []).map((item) => ({
    date: item.date,
    quantity: item.waste_quantity || 0,
    value: item.monetary_loss || 0,
    transaction_count: item.incident_count || 0,
  }));

  // Transform category breakdown
  const categoryBreakdown = apiData.categoryBreakdown || {};
  const categoryNameMap = {
    'prod_waste': 'Production Waste',
    'serv_waste': 'Service Waste',
    'exp_waste': 'Expired Waste',
  };
  const wastageByCategory = Object.keys(categoryBreakdown).map((key) => {
    const cat = categoryBreakdown[key];
    const totalQuantity = apiData.summary?.total_waste_quantity || 0;
    const totalValue = apiData.summary?.total_monetary_loss || 0;
    return {
      category: categoryNameMap[key] || key,
      quantity: cat.quantity || 0,
      value: cat.monetary_loss || 0,
      percentage: totalValue > 0 ? ((cat.monetary_loss / totalValue) * 100).toFixed(1) : 0,
    };
  });

  // Transform item-wise wastage
  const wastageByItem = (apiData.itemWiseWastage || []).map((item) => ({
    item_id: item.stock_item_id,
    item_name: item.item_name,
    quantity: item.total_waste_quantity || 0,
    value: item.monetary_loss || 0,
    unit: item.unit_of_measure || '',
    category: Object.keys(item.category_breakdown || {})[0] || 'Unknown',
  }));

  // Transform summary
  const summary = {
    total_wastage_quantity: apiData.summary?.total_waste_quantity || 0,
    total_wastage_value: apiData.summary?.total_monetary_loss || 0,
    avg_wastage_value: daywiseWastage.length > 0 
      ? (apiData.summary?.total_monetary_loss || 0) / daywiseWastage.length 
      : 0,
    most_wastage: apiData.topWastedItems && apiData.topWastedItems.length > 0
      ? {
          name: apiData.topWastedItems[0].item_name,
          quantity: apiData.topWastedItems[0].total_waste_quantity,
          unit: apiData.itemWiseWastage?.find(i => i.stock_item_id === apiData.topWastedItems[0].stock_item_id)?.unit_of_measure || '',
          value: apiData.topWastedItems[0].monetary_loss,
        }
      : null,
    least_wastage: apiData.leastWastedItems && apiData.leastWastedItems.length > 0
      ? {
          name: apiData.leastWastedItems[0].item_name,
          quantity: apiData.leastWastedItems[0].total_waste_quantity,
          unit: apiData.itemWiseWastage?.find(i => i.stock_item_id === apiData.leastWastedItems[0].stock_item_id)?.unit_of_measure || '',
          value: apiData.leastWastedItems[0].monetary_loss,
        }
      : null,
  };

  // For site breakdown, we need to aggregate data from all sites
  // This is a simplified version - in a real scenario, you might need to call API for each site
  const siteBreakdownWithCategories = sites.map((site) => {
    // This would ideally come from separate API calls per site
    // For now, we'll create a placeholder structure
    const breakdown = {};
    wastageByCategory.forEach((cat) => {
      breakdown[cat.category] = {
        quantity: 0,
        value: 0,
      };
    });
    return {
      site_id: site.site_id || site.id || site._id,
      site_name: site.site_name || site.name,
      categories: breakdown,
      total_quantity: 0,
      total_value: 0,
    };
  });

  return {
    period: apiData.period || {},
    summary,
    daywise_wastage: daywiseWastage,
    site_breakdown_with_categories: siteBreakdownWithCategories,
    wastage_by_category: wastageByCategory,
    wastage_by_item: wastageByItem,
  };
};

// Generate comprehensive dummy wastage dashboard data
const generateDummyWastageData = (period, startDate, endDate, selectedSiteId) => {
  const sites = [
    { id: 'site-001', name: 'Koramangala Outlet' },
    { id: 'site-002', name: 'Indiranagar Branch' },
    { id: 'site-003', name: 'Whitefield Center' },
    { id: 'site-004', name: 'MG Road Store' },
    { id: 'site-005', name: 'Electronic City' },
  ];

  const categories = [
    'Food Items',
    'Beverages',
    'Dairy Products',
    'Vegetables',
    'Fruits',
    'Meat & Poultry',
    'Bakery Items',
    'Packaged Goods',
    'Spices & Condiments',
    'Frozen Foods',
    'Snacks & Chips',
    'Canned Goods',
    'Cleaning Supplies',
    'Personal Care',
    'Stationery Items',
  ];

  const wastageItems = [
    { id: 'item-001', name: 'Paneer (1kg)', unit: 'kg', basePrice: 350 },
    { id: 'item-002', name: 'Bread (Loaf)', unit: 'piece', basePrice: 45 },
    { id: 'item-003', name: 'Tomatoes (1kg)', unit: 'kg', basePrice: 60 },
    { id: 'item-004', name: 'Onions (1kg)', unit: 'kg', basePrice: 40 },
    { id: 'item-005', name: 'Potatoes (1kg)', unit: 'kg', basePrice: 35 },
    { id: 'item-006', name: 'Milk (1L)', unit: 'litre', basePrice: 55 },
    { id: 'item-007', name: 'Cheese (500g)', unit: 'g', basePrice: 280 },
    { id: 'item-008', name: 'Butter (500g)', unit: 'g', basePrice: 320 },
    { id: 'item-009', name: 'Eggs (Dozen)', unit: 'dozen', basePrice: 90 },
    { id: 'item-010', name: 'Chicken (1kg)', unit: 'kg', basePrice: 250 },
    { id: 'item-011', name: 'Rice (1kg)', unit: 'kg', basePrice: 80 },
    { id: 'item-012', name: 'Wheat Flour (1kg)', unit: 'kg', basePrice: 50 },
    { id: 'item-013', name: 'Sugar (1kg)', unit: 'kg', basePrice: 45 },
    { id: 'item-014', name: 'Salt (1kg)', unit: 'kg', basePrice: 20 },
    { id: 'item-015', name: 'Oil (1L)', unit: 'litre', basePrice: 140 },
  ];

  // Calculate date range
  let days = 7; // Default for weekly
  if (period === 'daily') days = 1;
  else if (period === 'weekly') days = 7;
  else if (period === 'monthly') days = 30;
  else if (period === 'custom' && startDate && endDate) {
    days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  }

  const dateArray = [];
  const baseDate = startDate || new Date();
  for (let i = 0; i < days; i++) {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + i);
    dateArray.push(formatDateForAPI(date));
  }

  // Generate daywise wastage analytics
  const daywiseWastage = dateArray.map((date) => {
    const quantity = 50 + Math.random() * 200;
    const value = quantity * (30 + Math.random() * 20);
    return {
      date,
      quantity: Math.round(quantity * 100) / 100,
      value: Math.round(value),
      transaction_count: Math.floor(Math.random() * 15) + 3,
    };
  });

  // Generate site breakdown with category details (only when all sites selected)
  const siteBreakdownWithCategories = sites.map((site) => {
    const breakdown = {};
    categories.forEach((category) => {
      const quantity = 10 + Math.random() * 80;
      const value = quantity * (25 + Math.random() * 15);
      breakdown[category] = {
        quantity: Math.round(quantity * 100) / 100,
        value: Math.round(value),
      };
    });
    return {
      site_id: site.id,
      site_name: site.name,
      categories: breakdown,
      total_quantity: Object.values(breakdown).reduce(
        (sum, cat) => sum + cat.quantity,
        0
      ),
      total_value: Object.values(breakdown).reduce(
        (sum, cat) => sum + cat.value,
        0
      ),
    };
  });

  // Generate wastage by item
  const wastageByItem = wastageItems.map((item) => {
    const quantity = 5 + Math.random() * 50;
    const value = quantity * item.basePrice;
    return {
      item_id: item.id,
      item_name: item.name,
      quantity: Math.round(quantity * 100) / 100,
      value: Math.round(value),
      unit: item.unit,
      category: categories[Math.floor(Math.random() * categories.length)],
    };
  });

  // Generate wastage by category
  const wastageByCategory = categories.map((category) => {
    const quantity = 50 + Math.random() * 300;
    const value = quantity * (30 + Math.random() * 20);
    return {
      category,
      quantity: Math.round(quantity * 100) / 100,
      value: Math.round(value),
      percentage: 0, // Will be calculated later
    };
  });

  // Calculate percentages for category breakdown
  const totalCategoryValue = wastageByCategory.reduce(
    (sum, cat) => sum + cat.value,
    0
  );
  wastageByCategory.forEach((cat) => {
    cat.percentage =
      totalCategoryValue > 0
        ? ((cat.value / totalCategoryValue) * 100).toFixed(1)
        : 0;
  });

  // Calculate summary metrics
  const totalWastageQuantity = daywiseWastage.reduce(
    (sum, d) => sum + d.quantity,
    0
  );
  const totalWastageValue = daywiseWastage.reduce((sum, d) => sum + d.value, 0);
  const avgWastageValue =
    daywiseWastage.length > 0
      ? totalWastageValue / daywiseWastage.length
      : 0;

  // Find most and least wastage items
  const sortedItems = [...wastageByItem].sort((a, b) => b.quantity - a.quantity);
  const mostWastage = sortedItems[0];
  const leastWastage = sortedItems[sortedItems.length - 1];

  return {
    period: {
      type: period,
      date: period !== 'custom' ? dateArray[0] : null,
      start_date:
        period === 'custom' && startDate
          ? startDate.toISOString()
          : dateArray[0] + 'T00:00:00Z',
      end_date:
        period === 'custom' && endDate
          ? endDate.toISOString()
          : dateArray[dateArray.length - 1] + 'T23:59:59Z',
      site_id: selectedSiteId || null,
      site_name: selectedSiteId
        ? sites.find((s) => s.id === selectedSiteId)?.name
        : null,
    },
    summary: {
      total_wastage_quantity: Math.round(totalWastageQuantity * 100) / 100,
      total_wastage_value: Math.round(totalWastageValue),
      avg_wastage_value: Math.round(avgWastageValue),
      most_wastage: mostWastage
        ? {
            name: mostWastage.item_name,
            quantity: mostWastage.quantity,
            unit: mostWastage.unit,
            value: mostWastage.value,
          }
        : null,
      least_wastage: leastWastage
        ? {
            name: leastWastage.item_name,
            quantity: leastWastage.quantity,
            unit: leastWastage.unit,
            value: leastWastage.value,
          }
        : null,
    },
    daywise_wastage: daywiseWastage,
    site_breakdown_with_categories: siteBreakdownWithCategories,
    wastage_by_category: wastageByCategory,
    wastage_by_item: wastageByItem,
  };
};

const WastageDashboard = () => {
  // Log screen view
  useScreenLogging('WastageDashboard', {
    component: 'WastageDashboard',
  });
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(''); // Default to "All Sites"
  const [period, setPeriod] = useState('weekly');
  const [selectedDate, setSelectedDate] = useState(formatDateForAPI(getCurrentDate()));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wastageData, setWastageData] = useState(null);
  const [error, setError] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState('');
  
  // Separate state for Site Breakdown chart filters (top filters)
  const [siteBreakdownData, setSiteBreakdownData] = useState(null);
  const [isSiteBreakdownLoading, setIsSiteBreakdownLoading] = useState(false);
  const [siteBreakdownPeriod, setSiteBreakdownPeriod] = useState('weekly');
  const [siteBreakdownDate, setSiteBreakdownDate] = useState(formatDateForAPI(getCurrentDate()));
  const [siteBreakdownStartDate, setSiteBreakdownStartDate] = useState(null);
  const [siteBreakdownEndDate, setSiteBreakdownEndDate] = useState(null);
  const [siteBreakdownView, setSiteBreakdownView] = useState('chart'); // 'chart' or 'heatmap'

  // Fetch sites on component mount (with fallback to dummy sites)
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sorted.length > 0 ? sorted : [
          { site_id: 'site-001', site_name: 'Koramangala Outlet' },
          { site_id: 'site-002', site_name: 'Indiranagar Branch' },
          { site_id: 'site-003', site_name: 'Whitefield Center' },
          { site_id: 'site-004', site_name: 'MG Road Store' },
          { site_id: 'site-005', site_name: 'Electronic City' },
        ]);
      } catch (error) {
        console.error('Error fetching sites, using fallback:', error);
        // Use dummy sites as fallback
        setSites([
          { site_id: 'site-001', site_name: 'Koramangala Outlet' },
          { site_id: 'site-002', site_name: 'Indiranagar Branch' },
          { site_id: 'site-003', site_name: 'Whitefield Center' },
          { site_id: 'site-004', site_name: 'MG Road Store' },
          { site_id: 'site-005', site_name: 'Electronic City' },
        ]);
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Don't auto-select - let user select from dropdown

  // Load Site Breakdown data based on top filters (period and date) - Using dummy data only
  useEffect(() => {
    const loadSiteBreakdownData = () => {
      if (sites.length === 0) return; // Wait for sites to load
      
      setIsSiteBreakdownLoading(true);
      
      try {
        let dateToUse = siteBreakdownDate ? new Date(siteBreakdownDate) : getCurrentDate();
        let startDateToUse = siteBreakdownStartDate;
        let endDateToUse = siteBreakdownEndDate;

        if (siteBreakdownPeriod === 'custom') {
          if (!startDateToUse || !endDateToUse) {
            setIsSiteBreakdownLoading(false);
            return;
          }
          startDateToUse = startDateToUse instanceof Date ? startDateToUse : new Date(startDateToUse);
          endDateToUse = endDateToUse instanceof Date ? endDateToUse : new Date(endDateToUse);
        } else {
          // For weekly/monthly, calculate date range
          if (siteBreakdownPeriod === 'weekly') {
            const dayOfWeek = dateToUse.getDay();
            const diff = dateToUse.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
            startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), diff);
            endDateToUse = new Date(startDateToUse);
            endDateToUse.setDate(startDateToUse.getDate() + 6);
          } else if (siteBreakdownPeriod === 'monthly') {
            startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), 1);
            endDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth() + 1, 0);
          }
        }

        // Generate dummy data for site breakdown
        const data = generateDummyWastageData(
          siteBreakdownPeriod,
          startDateToUse,
          endDateToUse,
          null
        );
        setSiteBreakdownData(data);
      } catch (err) {
        console.error('Error loading site breakdown data:', err);
      } finally {
        setIsSiteBreakdownLoading(false);
      }
    };

    loadSiteBreakdownData();
  }, [siteBreakdownPeriod, siteBreakdownDate, siteBreakdownStartDate, siteBreakdownEndDate, sites]);

  // Load wastage data when filters change
  useEffect(() => {
    const loadWastageData = async () => {
      // If no site is selected, show message or use fallback
      if (!selectedSite) {
        setWastageData(null);
        setError('Please select a site to view wastage data');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let dateToUse = selectedDate ? new Date(selectedDate) : getCurrentDate();
        let startDateToUse = startDate;
        let endDateToUse = endDate;
        let dateParam = null;

        if (period === 'custom') {
          if (!startDateToUse || !endDateToUse) {
            setIsLoading(false);
            return;
          }
          startDateToUse = startDateToUse instanceof Date ? startDateToUse : new Date(startDateToUse);
          endDateToUse = endDateToUse instanceof Date ? endDateToUse : new Date(endDateToUse);
        } else {
          // For weekly/monthly, calculate date range
          if (period === 'weekly') {
            const dayOfWeek = dateToUse.getDay();
            const diff = dateToUse.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
            startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), diff);
            endDateToUse = new Date(startDateToUse);
            endDateToUse.setDate(startDateToUse.getDate() + 6);
            dateParam = formatDateForAPI(dateToUse);
          } else if (period === 'monthly') {
            startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), 1);
            endDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth() + 1, 0);
            dateParam = `${dateToUse.getFullYear()}-${String(dateToUse.getMonth() + 1).padStart(2, '0')}`;
          } else {
            // daily
            startDateToUse = new Date(dateToUse);
            endDateToUse = new Date(dateToUse);
            dateParam = formatDateForAPI(dateToUse);
          }
        }

        // Call API based on period type
        let result;
        if (period === 'custom') {
          result = await wastageApi.getCustomRangeDashboard(
            selectedSite,
            formatDateForAPI(startDateToUse),
            formatDateForAPI(endDateToUse)
          );
        } else if (period === 'weekly') {
          result = await wastageApi.getWeeklyDashboard(selectedSite, dateParam);
        } else if (period === 'monthly') {
          result = await wastageApi.getMonthlyDashboard(selectedSite, dateParam);
        } else {
          // daily
          result = await wastageApi.getDailyDashboard(selectedSite, dateParam);
        }

        if (result.success && result.data) {
          // Transform API response to component format
          const transformedData = transformApiResponseToComponentFormat(result.data, sites);
          if (transformedData) {
            setWastageData(transformedData);
            setCarouselIndex(0); // Reset carousel when data changes
          } else {
            throw new Error('Failed to transform API response');
          }
        } else {
          throw new Error(result.message || 'Failed to load wastage dashboard data');
        }
      } catch (err) {
        console.error('Error loading wastage dashboard data:', err);
        setError(err.message || 'Failed to load wastage dashboard data');
        
        // Fallback to dummy data on error
        try {
          let dateToUse = selectedDate ? new Date(selectedDate) : getCurrentDate();
          let startDateToUse = startDate;
          let endDateToUse = endDate;

          if (period === 'custom') {
            if (!startDateToUse || !endDateToUse) {
              setIsLoading(false);
              return;
            }
          } else {
            if (period === 'weekly') {
              const dayOfWeek = dateToUse.getDay();
              const diff = dateToUse.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
              startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), diff);
              endDateToUse = new Date(startDateToUse);
              endDateToUse.setDate(startDateToUse.getDate() + 6);
            } else if (period === 'monthly') {
              startDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth(), 1);
              endDateToUse = new Date(dateToUse.getFullYear(), dateToUse.getMonth() + 1, 0);
            } else {
              startDateToUse = new Date(dateToUse);
              endDateToUse = new Date(dateToUse);
            }
          }

          const data = generateDummyWastageData(
            period,
            startDateToUse,
            endDateToUse,
            selectedSite || null
          );
          setWastageData(data);
        } catch (fallbackErr) {
          console.error('Error in fallback data generation:', fallbackErr);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadWastageData();
  }, [period, selectedDate, startDate, endDate, selectedSite, sites]);

  // Chart data preparation
  // Site Breakdown chart - uses separate data, always shows all sites
  const siteBreakdownChartData = useMemo(() => {
    if (!siteBreakdownData?.site_breakdown_with_categories) {
      return null;
    }

    // Get all unique categories from the data
    const allCategories = new Set();
    siteBreakdownData.site_breakdown_with_categories.forEach((site) => {
      Object.keys(site.categories || {}).forEach((cat) => allCategories.add(cat));
    });
    const categories = Array.from(allCategories);

    const categoryColors = [
      '#9333EA',
      '#14B8A6',
      '#2563EB',
      '#EF4444',
      '#F59E0B',
      '#06B6D4',
      '#8B5CF6',
      '#F472B6',
      '#84CC16',
      '#EC4899',
      '#6366F1',
      '#F97316',
      '#10B981',
      '#3B82F6',
      '#A855F7',
    ];

    // Always show all sites (not affected by filter)
    const datasets = categories.map((category, index) => ({
      label: category,
      data: siteBreakdownData.site_breakdown_with_categories.map((site) =>
        site.categories[category]?.quantity || 0
      ),
      backgroundColor: categoryColors[index % categoryColors.length],
    }));

    return {
      labels: siteBreakdownData.site_breakdown_with_categories.map((s) => s.site_name),
      datasets,
    };
  }, [siteBreakdownData]); // Only depends on siteBreakdownData, not selectedSite

  // Heatmap data preparation for Site Breakdown
  const siteBreakdownHeatmapData = useMemo(() => {
    if (!siteBreakdownData?.site_breakdown_with_categories) {
      return null;
    }

    // Get all unique categories from the data
    const allCategories = new Set();
    siteBreakdownData.site_breakdown_with_categories.forEach((site) => {
      Object.keys(site.categories || {}).forEach((cat) => allCategories.add(cat));
    });
    const categories = Array.from(allCategories);

    // Get all values to calculate min/max for color intensity
    const allValues = [];
    siteBreakdownData.site_breakdown_with_categories.forEach((site) => {
      categories.forEach((category) => {
        const value = site.categories[category]?.quantity || 0;
        allValues.push(value);
      });
    });

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue || 1;

    // Prepare data for heatmap
    const sites = siteBreakdownData.site_breakdown_with_categories.map((site) => {
      const categoryData = {};
      let total = 0;
      
      categories.forEach((category) => {
        const value = site.categories[category]?.quantity || 0;
        total += value;
        categoryData[category] = {
          value,
          intensity: range > 0 ? (value - minValue) / range : 0, // 0 to 1
        };
      });

      return {
        site_name: site.site_name,
        site_id: site.site_id,
        categories: categoryData,
        total,
      };
    });

    return {
      sites,
      categories,
      minValue,
      maxValue,
    };
  }, [siteBreakdownData]);

  const daywiseWastageChartData = useMemo(() => {
    if (!wastageData?.daywise_wastage) return null;

    const sorted = [...wastageData.daywise_wastage].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Wastage Amount (₹)',
          data: sorted.map((item) => item.value || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [wastageData]);

  const wastageValueChartData = useMemo(() => {
    if (!wastageData?.wastage_by_category) return null;

    const categoryColors = [
      '#9333EA',
      '#14B8A6',
      '#2563EB',
      '#EF4444',
      '#F59E0B',
      '#06B6D4',
      '#8B5CF6',
      '#F472B6',
      '#84CC16',
      '#EC4899',
      '#6366F1',
      '#F97316',
      '#10B981',
      '#3B82F6',
      '#A855F7',
    ];

    return {
      labels: wastageData.wastage_by_category.map((cat) => cat.category),
      datasets: [
        {
          data: wastageData.wastage_by_category.map((cat) => cat.value || 0),
          backgroundColor: wastageData.wastage_by_category.map(
            (_, index) => categoryColors[index % categoryColors.length]
          ),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    };
  }, [wastageData]);

  const wastageByItemChartData = useMemo(() => {
    if (!wastageData?.wastage_by_item) return null;

    // Sort items by value (amount wasted) in descending order
    const sortedItems = [...wastageData.wastage_by_item].sort(
      (a, b) => (b.value || 0) - (a.value || 0)
    );

    return {
      labels: sortedItems.map((item) => item.item_name),
      datasets: [
        {
          label: 'Amount Wasted (₹)',
          data: sortedItems.map((item) => item.value || 0),
          backgroundColor: '#ef4444',
          borderColor: '#dc2626',
          borderWidth: 1,
        },
      ],
    };
  }, [wastageData]);

  const itemSpecificDaywiseChartData = useMemo(() => {
    if (!wastageData?.daywise_wastage || !wastageData?.wastage_by_item || !selectedItem) {
      return null;
    }

    // Find the selected item
    const selectedItemData = wastageData.wastage_by_item.find(
      (item) => String(item.item_id) === String(selectedItem)
    );

    if (!selectedItemData) return null;

    // Generate daywise wastage for the selected item
    // Distribute the total wastage value across the date range proportionally
    const totalDays = wastageData.daywise_wastage.length;
    const avgDailyWastage = selectedItemData.value / totalDays;

    const sorted = [...wastageData.daywise_wastage].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    // Generate daywise data with some variation
    const daywiseData = sorted.map((day, index) => {
      // Create variation: 70% to 130% of average
      const variation = 0.7 + Math.random() * 0.6;
      const dailyValue = avgDailyWastage * variation;
      return {
        date: day.date,
        value: Math.round(dailyValue),
      };
    });

    return {
      labels: daywiseData.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: `${selectedItemData.item_name} - Amount Wasted (₹)`,
          data: daywiseData.map((item) => item.value || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [wastageData, selectedItem]);

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  const stackedBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN')} units`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return value.toLocaleString('en-IN');
          },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend since we show items on the right
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 10,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ₹${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  const wastageByItemBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function (context) {
            const item = wastageData?.wastage_by_item?.find(
              (item) => item.item_name === context.label
            );
            const quantity = item?.quantity || 0;
            const unit = item?.unit || '';
            return [
              `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`,
              `Quantity: ${quantity.toLocaleString('en-IN')} ${unit}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount Wasted (₹)',
          font: {
            size: 12,
            weight: '500',
          },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  const itemSpecificLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date Range',
          font: {
            size: 12,
            weight: '500',
          },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount Wasted (₹)',
          font: {
            size: 12,
            weight: '500',
          },
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  // Summary statistics
  const summary = wastageData?.summary || {};

  return (
    <div className="w-full h-full">
      {/* Header with Filters - Only for Site Breakdown Chart */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Wastage Dashboard</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Comprehensive wastage analytics and insights
          </p>
        </div>

        {/* Filters - Only for Site Breakdown with Category Details */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-600">Filter Site Breakdown:</span>
          {/* Period Selector */}
          <select
            value={siteBreakdownPeriod}
            onChange={(e) => {
              setSiteBreakdownPeriod(e.target.value);
              if (e.target.value !== 'custom') {
                setSiteBreakdownStartDate(null);
                setSiteBreakdownEndDate(null);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom Range</option>
          </select>

          {siteBreakdownPeriod !== 'custom' ? (
            <input
              type="date"
              value={siteBreakdownDate}
              onChange={(e) => setSiteBreakdownDate(e.target.value)}
              className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          ) : (
            <>
              <input
                type="date"
                value={siteBreakdownStartDate ? formatDateForAPI(siteBreakdownStartDate) : ''}
                onChange={(e) => setSiteBreakdownStartDate(e.target.value ? new Date(e.target.value) : null)}
                placeholder="Start Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
              <input
                type="date"
                value={siteBreakdownEndDate ? formatDateForAPI(siteBreakdownEndDate) : ''}
                onChange={(e) => setSiteBreakdownEndDate(e.target.value ? new Date(e.target.value) : null)}
                placeholder="End Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
            </>
          )}
        </div>
      </div>

      {/* Initial Loading State - Only shows when there's no data at all */}
      {isLoading && !wastageData && (
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading wastage analytics...</p>
          </div>
        </div>
      )}

      {/* Site Breakdown Chart - Independent Section */}
      <div className="mb-6">
        {isSiteBreakdownLoading ? (
          <Card.Gradient className="p-6">
            <div className="flex justify-center items-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading site breakdown...</p>
              </div>
            </div>
          </Card.Gradient>
        ) : (siteBreakdownChartData || siteBreakdownHeatmapData) ? (
          <Card.Gradient className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: 0 }}>
                Site Breakdown with Category Details
              </h3>
              
              {/* View Toggle Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSiteBreakdownView('chart')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    siteBreakdownView === 'chart'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Chart
                </button>
                <button
                  onClick={() => setSiteBreakdownView('heatmap')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    siteBreakdownView === 'heatmap'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Heatmap
                </button>
              </div>
            </div>

            {/* Conditional Rendering based on view */}
            {siteBreakdownView === 'chart' && siteBreakdownChartData ? (
              <div style={{ height: '400px' }}>
                <Bar data={siteBreakdownChartData} options={stackedBarChartOptions} />
              </div>
            ) : siteBreakdownView === 'heatmap' && siteBreakdownHeatmapData ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-white border border-gray-300 px-4 py-3 text-left font-semibold text-sm z-10">
                        Site
                      </th>
                      {siteBreakdownHeatmapData.categories.map((category) => (
                        <th
                          key={category}
                          className="border border-gray-300 px-3 py-3 text-center font-semibold text-xs min-w-[100px]"
                        >
                          {category}
                        </th>
                      ))}
                      <th className="border border-gray-300 px-4 py-3 text-center font-bold text-sm bg-gray-50">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteBreakdownHeatmapData.sites.map((site) => (
                      <tr key={site.site_id}>
                        <td className="sticky left-0 bg-white border border-gray-300 px-4 py-3 font-medium text-sm z-10">
                          {site.site_name}
                        </td>
                        {siteBreakdownHeatmapData.categories.map((category) => {
                          const cellData = site.categories[category];
                          const intensity = cellData.intensity;
                          // Color gradient from light blue/white (low) to dark red/pink (high)
                          const red = Math.min(255, Math.floor(220 + intensity * 35));
                          const green = Math.min(255, Math.floor(180 - intensity * 150));
                          const blue = Math.min(255, Math.floor(200 - intensity * 100));
                          const backgroundColor = `rgb(${red}, ${green}, ${blue})`;
                          const textColor = intensity > 0.5 ? 'white' : 'black';

                          return (
                            <td
                              key={category}
                              className="border border-gray-300 px-3 py-3 text-center text-sm font-medium"
                              style={{
                                backgroundColor,
                                color: textColor,
                              }}
                              title={`${category}: ${cellData.value.toFixed(2)} units`}
                            >
                              {cellData.value.toFixed(2)}
                            </td>
                          );
                        })}
                        <td className="border border-gray-300 px-4 py-3 text-center font-bold text-sm bg-gray-50">
                          {site.total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Legend */}
                <div className="mt-4 text-xs text-gray-600 text-center">
                  Color intensity represents wastage amount. Darker = Higher wastage.
                </div>
              </div>
            ) : null}
          </Card.Gradient>
        ) : null}
      </div>

      {/* Site Filter Dropdown and Date Picker - Always Visible */}
      <div className="w-full mb-6">
        <h3 className={`${typography.h3} text-black mb-4`}>
          Filter Wastage based on Site
        </h3>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Site Dropdown */}
          <div className="flex-1 max-w-md">
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              disabled={isSitesLoading}
              className="block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
            >
              {isSitesLoading ? (
                <option>Loading sites...</option>
              ) : (
                <>
                  <option value="">Select a site</option>
                  {sites.map((site) => {
                    const siteId =
                      site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? '';
                    const siteName = site?.site_name || site?.name || `Site ${siteId}`;
                    return (
                      <option key={siteId} value={String(siteId)}>
                        {siteName}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
          </div>

          {/* Period Selector */}
          <div className="flex-1 max-w-md">
            <select
              value={period}
              onChange={(e) => {
                setPeriod(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
              className="block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Date Picker */}
          <div className="flex-1 max-w-md">
            {period !== 'custom' ? (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
              />
            ) : (
              <div className="flex gap-3">
                <input
                  type="date"
                  value={startDate ? formatDateForAPI(startDate) : ''}
                  onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                  placeholder="Start Date"
                  className="flex-1 h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                />
                <input
                  type="date"
                  value={endDate ? formatDateForAPI(endDate) : ''}
                  onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                  placeholder="End Date"
                  className="flex-1 h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts Grid - Show previous data while loading */}
      {error && !isLoading && (
        <Card.Gradient className="p-8 text-center mb-6">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className={typography.h4} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              Error Loading Data
            </h3>
            <p className={typography.p} style={{ color: '#666666' }}>
              {error}
            </p>
          </div>
        </Card.Gradient>
      )}

      {/* Charts Grid - Show previous data while loading */}
      {wastageData && (
        <div className="space-y-6 relative">
          {/* Loading Overlay - Only shows when loading new data */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Updating charts...</p>
              </div>
            </div>
          )}

          {/* ROW-2: KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CARD-1: Total Wastage (Qty per Value) */}
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #ef4444, #dc2626)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>
                Total Wastage
              </p>
              <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
                ₹{summary.total_wastage_value?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
                {summary.total_wastage_quantity?.toLocaleString('en-IN') || '0'} units
              </p>
            </Card.Gradient>

            {/* CARD-2: Avg Wastage Value */}
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #f59e0b, #d97706)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>
                Avg Wastage Value
              </p>
              <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
                ₹{summary.avg_wastage_value?.toLocaleString('en-IN') || '0'}
              </p>
              <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
                Per day average
              </p>
            </Card.Gradient>

            {/* CARD-3: Most Wastage */}
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #dc2626, #b91c1c)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>
                Most Wastage
              </p>
              <p className="text-lg font-bold" style={{ color: '#ffffff' }}>
                {summary.most_wastage?.name || 'N/A'}
              </p>
              <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
                {summary.most_wastage?.quantity?.toLocaleString('en-IN') || '0'}{' '}
                {summary.most_wastage?.unit || ''}
              </p>
            </Card.Gradient>

            {/* CARD-4: Least Wastage */}
            <Card.Gradient
              className="p-5"
              style={{
                background: 'linear-gradient(to bottom right, #10b981, #059669)',
                boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
                color: '#ffffff',
              }}
            >
              <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>
                Least Wastage
              </p>
              <p className="text-lg font-bold" style={{ color: '#ffffff' }}>
                {summary.least_wastage?.name || 'N/A'}
              </p>
              <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
                {summary.least_wastage?.quantity?.toLocaleString('en-IN') || '0'}{' '}
                {summary.least_wastage?.unit || ''}
              </p>
            </Card.Gradient>
          </div>

          {/* ROW-3: Charts */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* COL-1: Day wise wastage (Line Chart) - 40% width */}
            {daywiseWastageChartData && (
              <Card.Gradient className="p-6 flex-[0_1_40%] min-w-0">
                <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                  Day wise Wastage
                </h3>
                <div style={{ height: '350px' }}>
                  <Line data={daywiseWastageChartData} options={lineChartOptions} />
                </div>
              </Card.Gradient>
            )}

            {/* COL-2: Wastage Value (Pie/Doughnut Chart) - Flex remaining space */}
            {wastageValueChartData && (
              <Card.Gradient className="p-6 flex-1 min-w-0">
                <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                  Wastage Value
                </h3>
                <div className="flex flex-col lg:flex-row gap-6 items-center">
                  <div className="w-full lg:w-[280px] h-[280px] relative">
                    <Doughnut data={wastageValueChartData} options={doughnutChartOptions} />
                  </div>
                  <div className="flex-1 w-full">
                    {wastageData.wastage_by_category.length > 8 ? (
                      // Carousel for more than 8 items
                      <div className="relative">
                        <div className="overflow-hidden">
                          <div
                            className="flex transition-transform duration-300 ease-in-out"
                            style={{
                              transform: `translateX(-${carouselIndex * 100}%)`,
                            }}
                          >
                            {Array.from({
                              length: Math.ceil(wastageData.wastage_by_category.length / 8),
                            }).map((_, slideIndex) => (
                              <div
                                key={slideIndex}
                                className="min-w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-8"
                              >
                                {wastageData.wastage_by_category
                                  .slice(slideIndex * 8, slideIndex * 8 + 8)
                                  .map((item, itemIndex) => {
                                    const actualIndex = slideIndex * 8 + itemIndex;
                                    return (
                                      <div
                                        key={item.category}
                                        className="flex items-center gap-3"
                                      >
                                        <div
                                          className="w-4 h-4 rounded-sm flex-shrink-0"
                                          style={{
                                            backgroundColor:
                                              wastageValueChartData.datasets[0].backgroundColor[
                                                actualIndex
                                              ],
                                          }}
                                        />
                                        <div className="flex flex-col gap-0.5">
                                          <p
                                            className={typography.p}
                                            style={{ color: '#000000', fontSize: '0.875rem' }}
                                          >
                                            {item.category}
                                          </p>
                                          <p
                                            className={typography.p}
                                            style={{ color: '#666666', fontSize: '0.75rem' }}
                                          >
                                            ₹{item.value.toLocaleString('en-IN')} ({item.percentage}%)
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between mt-4">
                          <button
                            type="button"
                            onClick={() =>
                              setCarouselIndex((prev) =>
                                prev > 0 ? prev - 1 : Math.ceil(wastageData.wastage_by_category.length / 8) - 1
                              )
                            }
                            className="p-2 rounded-full bg-white/80 hover:bg-white border border-gray-300 shadow-sm transition-colors"
                            aria-label="Previous slide"
                          >
                            <svg
                              className="w-5 h-5 text-gray-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </button>
                          {/* Dots Indicator */}
                          <div className="flex gap-2">
                            {Array.from({
                              length: Math.ceil(wastageData.wastage_by_category.length / 8),
                            }).map((_, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => setCarouselIndex(index)}
                                className={`h-2 rounded-full transition-all ${
                                  carouselIndex === index
                                    ? 'w-8 bg-green-600'
                                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                              />
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setCarouselIndex((prev) =>
                                prev < Math.ceil(wastageData.wastage_by_category.length / 8) - 1
                                  ? prev + 1
                                  : 0
                              )
                            }
                            className="p-2 rounded-full bg-white/80 hover:bg-white border border-gray-300 shadow-sm transition-colors"
                            aria-label="Next slide"
                          >
                            <svg
                              className="w-5 h-5 text-gray-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Regular grid for 8 or fewer items
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                        {wastageData.wastage_by_category.map((item, index) => (
                          <div key={item.category} className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-sm flex-shrink-0"
                              style={{
                                backgroundColor:
                                  wastageValueChartData.datasets[0].backgroundColor[index],
                              }}
                            />
                            <div className="flex flex-col gap-0.5">
                              <p
                                className={typography.p}
                                style={{ color: '#000000', fontSize: '0.875rem' }}
                              >
                                {item.category}
                              </p>
                              <p
                                className={typography.p}
                                style={{ color: '#666666', fontSize: '0.75rem' }}
                              >
                                ₹{item.value.toLocaleString('en-IN')} ({item.percentage}%)
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* ROW-4: Wastage for each stock */}
          {wastageByItemChartData && (
            <Card.Gradient className="p-6">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Wastage for each stock
              </h3>
              <div style={{ height: '400px' }}>
                <Bar data={wastageByItemChartData} options={wastageByItemBarChartOptions} />
              </div>
            </Card.Gradient>
          )}

          {/* ROW-5: Wastage of a particular stock */}
          {wastageData?.wastage_by_item && (
            <Card.Gradient className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <h3 className={typography.h5} style={{ color: '#000000' }}>
                  Wastage of a particular stock
                </h3>
                <div className="flex-shrink-0">
                  <select
                    value={selectedItem}
                    onChange={(e) => setSelectedItem(e.target.value)}
                    className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm min-w-[200px]"
                  >
                    <option value="">Select a stock/item</option>
                    {wastageData.wastage_by_item
                      .sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''))
                      .map((item) => (
                        <option key={item.item_id} value={String(item.item_id)}>
                          {item.item_name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              {itemSpecificDaywiseChartData ? (
                <div style={{ height: '400px' }}>
                  <Line data={itemSpecificDaywiseChartData} options={itemSpecificLineChartOptions} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-[400px] text-gray-500">
                  <p>Please select a stock/item to view wastage trends</p>
                </div>
              )}
            </Card.Gradient>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !wastageData && !error && (
        <Card.Gradient className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className={typography.h4} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              No wastage data available
            </h3>
            <p className={typography.p} style={{ color: '#666666' }}>
              Please select a different date range or site to view analytics
            </p>
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default WastageDashboard;

